import type { AlternativeOptions } from '@features/recipe-tile'
import type { Recipe, RecipeCoreId, SupportedOptionType } from '@library/menu-service'
import type { MenuRecipe } from '@library/menu-service/queries/menuApiMenuRecipe/types'

import { useRecipes } from 'hooks/menu-recipes/useRecipes'
import { useRecipeDetailData } from 'routes/Menu/components/Detail/hooks/useRecipeDetailData'

type UseRecipeOptionsV3Input = {
  mainRecipe: Recipe
  shouldFetchOptions: boolean
  menuId: string
  numPortions: number
  deliveryDate: string
  collectionId?: string
  defaultOptionsType: SupportedOptionType
  basketHasRecipe: (recipeId: RecipeCoreId) => boolean
}

const compareCoreRecipeIds = (a: Recipe, b: Recipe) =>
  parseInt(a.coreId, 10) - parseInt(b.coreId, 10)

const renderRecipeName = (recipe: Recipe) =>
  [
    recipe.name,
    recipe.dietaryClaims.some((claim) => claim.slug === 'vegetarian' || claim.slug === 'vegan')
      ? '(V)'
      : undefined,
  ]
    .filter((part) => Boolean(part))
    .join(' ')

const buildV3AlternativeOptions = ({
  mainRecipe,
  fullRecipe,
  menuRecipes,
  basketHasRecipe,
}: {
  mainRecipe: Recipe
  fullRecipe: MenuRecipe
  menuRecipes: Recipe[]
  basketHasRecipe: (recipeId: RecipeCoreId) => boolean
}): AlternativeOptions => {
  const orderedOptions = menuRecipes.sort(compareCoreRecipeIds)

  const options = orderedOptions.map((recipe) => ({
    recipe,
    recipeId: recipe.coreId,
    recipeName: renderRecipeName(recipe),
    isChecked: mainRecipe.id === recipe.id,
    isOutOfStock: !recipe.isAvailable && !basketHasRecipe(recipe.coreId),
    surcharge: recipe.surcharge ? recipe.surcharge.pricePerPortion / 100 : null,
    ingredientSlotValues: fullRecipe.options?.find((option) => option.item_id === recipe.id)
      ?.ingredient_slot_values,
  }))

  return {
    ingredientSlots: fullRecipe.ingredient_slots,
    options,
    optionsType: options.length > 0 ? fullRecipe.options_type : 'none',
  }
}

export const useRecipeOptionsV3 = ({
  mainRecipe,
  shouldFetchOptions,
  menuId,
  numPortions,
  deliveryDate,
  collectionId,
  defaultOptionsType,
  basketHasRecipe,
}: UseRecipeOptionsV3Input): {
  isLoadingOptions: boolean
  alternativeOptions: AlternativeOptions
} => {
  const {
    loading,
    recipe: v3Recipe,
    error,
  } = useRecipeDetailData({
    menuId,
    recipeUuid: mainRecipe.id,
    numPortions,
    deliveryDate,
    shouldFetchOptions,
    collectionId,
    isMenuExperienceRecipeDetailEnabled: false,
  })

  const menuRecipe = v3Recipe as MenuRecipe | null
  const optionIds =
    shouldFetchOptions && menuRecipe ? menuRecipe.options.map((option) => option.item_id) : []
  const menuRecipes: Recipe[] = useRecipes(optionIds).filter(
    (recipe): recipe is Recipe => recipe !== null,
  )

  if (!shouldFetchOptions || !menuRecipe || error) {
    return {
      isLoadingOptions: loading,
      alternativeOptions: {
        optionsType: defaultOptionsType,
        options: null,
      },
    }
  }

  return {
    isLoadingOptions: loading,
    alternativeOptions: buildV3AlternativeOptions({
      mainRecipe,
      fullRecipe: menuRecipe,
      menuRecipes,
      basketHasRecipe,
    }),
  }
}
